/**
 * Copyright schukai GmbH and contributors 2022. All Rights Reserved.
 * Node module: @schukai/component-form
 * This file is licensed under the AGPLv3 License.
 * License text available at https://www.gnu.org/licenses/agpl-3.0.en.html
 */

import { ATTRIBUTE_ROLE } from "../../dom/constants.mjs";
import {
	assembleMethodSymbol,
	CustomElement,
	registerCustomElement,
} from "../../dom/customelement.mjs";
import { validateInstance, validateString } from "../../types/validate.mjs";
import { Message } from "./message.mjs";
import { NotifyStyleSheet } from "./stylesheet/notify.mjs";
import { Queue } from "../../types/queue.mjs";
import { fireCustomEvent } from "../../dom/events.mjs";

export { Notify };

/**
 * @private
 * @type {symbol}
 */
const controlElementSymbol = Symbol("controlElement");

/**
 * @private
 * @type {symbol}
 */
const containerElementSymbol = Symbol("containerElement");

/**
 * @private
 * @type {symbol}
 */
const queueSymbol = Symbol("queue");

/**
 * This CustomControl creates a notification element with a variety of options.
 *
 * <img src="./images/notify.png">
 *
 * Dependencies: the system uses functions of the [monsterjs](https://monsterjs.org/) library.
 *
 * You can create this control either by specifying the HTML tag `<monster-notify />` directly in the HTML
 *
 * ```html
 * <monster-notify></monster-notify>
 * ```
 *
 * or using Javascript via the `document.createElement('monster-notify');` method.
 *
 * ```js
 * import '@schukai/monster/source/components/notify/notify.js';
 * document.createElement('monster-notify');
 * ```
 *
 * ## Events
 *
 * The CustomEvent has the property [`detail`](https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent/detail).
 *
 * ```
 * node.addEventListener('monster-notify-message',(e)=>console.log(e.detail))
 * ```
 *
 * @externalExample ../../../example/components/notify/notify.mjs
 * @startuml notify.png
 * skinparam monochrome true
 * skinparam shadowing false
 * HTMLElement <|-- CustomElement
 * CustomElement <|-- Notify
 * @enduml
 *
 * @since 1.0.0
 * @copyright schukai GmbH
 * @memberOf Monster.Components.Notify
 * @summary A highly configurable select control
 */
class Notify extends CustomElement {
	constructor() {
		super();
		this[queueSymbol] = new Queue();
	}

	/**
	 * The defaults can be set either directly in the object or via an attribute in the HTML tag.
	 * The value of the attribute `data-monster-options` in the HTML tag must be a JSON string.
	 *
	 * ```html
	 * <monster-notify data-monster-options="{}"></monster-notify>
	 * ```
	 *
	 * Since 1.18.0 the JSON can be specified as a DataURI.
	 *
	 * ```
	 * new Monster.Types.DataUrl(btoa(JSON.stringify({
	 *        orientation: 'right top'
	 *    })),'application/json',true).toString()
	 * ```
	 *
	 * @property {string} templates Template definitions
	 * @property {Object} templates Template definitions
	 * @property {string} templates.main Main template
	 */
	get defaults() {
		return Object.assign(
			{},
			super.defaults,
			{
				orientation: "left top",
				templates: {
					main: getTemplate(),
				},
			},
			initOptionsFromArguments.call(this),
		);
	}

	/**
	 *
	 * @return {Notify}
	 */
	[assembleMethodSymbol]() {
		super[assembleMethodSymbol]();
		initControlReferences.call(this);

		if (this[containerElementSymbol]) {
			while (this[queueSymbol].isEmpty() === false) {
				this.push(this[queueSymbol].poll());
			}
		}

		return this;
	}

	/**
	 *
	 * @return {string}
	 */
	static getTag() {
		return "monster-notify";
	}

	/**
	 *
	 * @return {CSSStyleSheet[]}
	 */
	static getCSSStyleSheet() {
		return [NotifyStyleSheet];
	}

	/**
	 *
	 * @param {Monster.Components.Notify.Massage|String} message
	 * @return {Monster.Components.Notify.Notify}
	 */
	push(message) {
		let messageElement = message;
		if (!(message instanceof Message)) {
			const text = validateString(message);
			messageElement = document.createElement("monster-notify-message");
			messageElement.setOption("content", text);
		}

		validateInstance(messageElement, Message);
		messageElement.setAttribute(ATTRIBUTE_ROLE, "message");

		// if not connected, add to queue
		if (!this[containerElementSymbol]) {
			this[queueSymbol].add(messageElement);
			return this;
		}

		fireCustomEvent(this, "monster-notify-message", {
			messageElement,
		});

		this[containerElementSymbol].appendChild(messageElement);
		return this;
	}
}

/**
 * This attribute can be used to pass a URL to this select.
 *
 * ```
 * <monster-select data-monster-url="https://example.com/"></monster-select>
 * ```
 *
 * @private
 * @return {object}
 */
function initOptionsFromArguments() {
	const options = {};

	return options;
}

/**
 * @private
 * @return {Select}
 * @throws {Error} no shadow-root is defined
 */
function initControlReferences() {
	if (!this.shadowRoot) {
		throw new Error("no shadow-root is defined");
	}

	this[controlElementSymbol] = this.shadowRoot.querySelector(
		"[" + ATTRIBUTE_ROLE + "=control]",
	);
	this[containerElementSymbol] = this.shadowRoot.querySelector(
		"[" + ATTRIBUTE_ROLE + "=container]",
	);
}

/**
 * @private
 * @return {string}
 */
function getTemplate() {
	// language=HTML
	return `
        <div data-monster-role="control" part="control" class="center"
             data-monster-attributes="data-monster-orientation path:orientation">
            <div data-monster-role="container">
            </div>
        </div>
    `;
}

registerCustomElement(Notify);
